import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/seo";
import { Card, Button, Pill } from "@/components/ui/Primitives";
import { Upload, Check, X, ArrowRight } from "lucide-react";

const fmtCAD = (cents) => {
  const v = (cents || 0) / 100;
  return (v < 0 ? "-CA$" : "CA$") + Math.abs(v).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function BankRecon() {
  usePageTitle("Bank reconciliation — Central Rentals");
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("unmatched");

  const load = async () => {
    setLoading(true);
    try {
      const matched = filter === "matched" ? true : filter === "unmatched" ? false : undefined;
      const q = matched === undefined ? "" : `?matched=${matched}`;
      const r = await api.get(`/accounting/bank/txns${q}`);
      setTxns(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/accounting/bank/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data?.imported || 0} rows · ${r.data?.skipped || 0} skipped`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not import CSV");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const applyMatch = async (txn, suggestion) => {
    try {
      await api.post(`/accounting/bank/txns/${txn.txn_id}/match`, {
        match_type: suggestion.type,
        match_id: suggestion.id,
      });
      toast.success("Matched.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not match");
    }
  };

  const ignore = async (txn) => {
    try {
      await api.post(`/accounting/bank/txns/${txn.txn_id}/match`, { match_type: "ignore" });
      toast.success("Ignored.");
      load();
    } catch (err) {
      toast.error("Could not ignore");
    }
  };

  return (
    <div className="space-y-6" data-testid="bank-recon-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1">Property Accounting</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Bank reconciliation</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Upload your bank statement CSV. We&apos;ll match deposits to rent dues and withdrawals to bills automatically.
            Review the exceptions below.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold cursor-pointer hover:bg-emerald-700" data-testid="bank-upload-label">
          <Upload className="w-4 h-4" />
          {uploading ? "Importing…" : "Import CSV"}
          <input type="file" accept=".csv,text/csv" onChange={upload} className="hidden" data-testid="bank-upload-input" disabled={uploading} />
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "unmatched", label: "Needs review" },
          { k: "matched", label: "Matched" },
          { k: "all", label: "All" },
        ].map(({ k, label }) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`bank-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              filter === k ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}>{label}</button>
        ))}
      </div>

      <Card>
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : txns.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="bank-empty">
            {filter === "unmatched" ? "No unmatched transactions — you're all reconciled 🎉" : "No transactions imported yet."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="bank-txn-list">
            {txns.map((t) => (
              <div key={t.txn_id} className="px-4 py-3 hover:bg-slate-50/60" data-testid={`bank-txn-${t.txn_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-slate-500 font-mono">{t.posted_at}</span>
                      {t.matched_type && <Pill tone="emerald">{t.matched_type}</Pill>}
                    </div>
                    <div className="text-sm text-slate-900 truncate">{t.description}</div>
                  </div>
                  <div className={`font-mono font-semibold text-right whitespace-nowrap ${t.amount_cents < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {fmtCAD(t.amount_cents)}
                  </div>
                </div>
                {!t.matched_type && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {t.suggestions?.length > 0 ? (
                      t.suggestions.map((s) => (
                        <button key={`${s.type}-${s.id}`} onClick={() => applyMatch(t, s)}
                          data-testid={`bank-match-${t.txn_id}`}
                          className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {s.label}
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-500 italic">No auto-match — review manually.</span>
                    )}
                    <button onClick={() => ignore(t)} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 ml-auto" data-testid={`bank-ignore-${t.txn_id}`}>
                      <X className="w-3 h-3" /> Ignore
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 bg-slate-50 border-slate-200">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">CSV format</div>
        <p className="text-xs text-slate-600 mb-2">Accepts most Canadian bank exports. Columns we look for (case-insensitive):</p>
        <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
          <li><strong>Date</strong> (or <em>Transaction Date</em>, <em>Posting Date</em>)</li>
          <li><strong>Description</strong> (or <em>Details</em>, <em>Memo</em>, <em>Merchant</em>)</li>
          <li><strong>Amount</strong> — OR separate <em>Deposit</em> + <em>Withdrawal</em> columns</li>
        </ul>
      </Card>
    </div>
  );
}
